import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, CreditCard, Truck, Shield, Loader } from 'lucide-react';
import api from '../lib/api';
import { formatPrice } from '../lib/format';

const Checkout = ({ cart, onBack, onOrderComplete, initialEmail, initialName, initialPhone }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [formData, setFormData] = useState({
    name: initialName || '',
    email: initialEmail || '',
    phone: initialPhone || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    landmark: ''
  });
  const [errors, setErrors] = useState({});

  // Normalize cart: ensure quantity is an integer >= 1
  const lineItems = useMemo(
    () => cart.map((it) => ({ ...it, quantity: Math.max(1, parseInt(it.quantity, 10) || 1) })),
    [cart]
  );

  // Indian states
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh'
  ];

  useEffect(() => {
    fetchPaymentConfig();
    loadRazorpayScript();
  }, []);

  const fetchPaymentConfig = async () => {
    try {
      const res = await api.get('/payments/config');
      if (res.data.success) {
        setPaymentConfig(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching payment config:', error);
    }
  };

  const loadRazorpayScript = () => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const calculatePricing = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shippingCharge = subtotal >= (paymentConfig?.freeShippingAbove || 999) ? 0 : (paymentConfig?.shippingCharge || 79);
    const codCharge = paymentMethod === 'cod' ? (paymentConfig?.codCharge || 49) : 0;
    const total = subtotal + shippingCharge + codCharge;

    return { subtotal, shippingCharge, codCharge, total };
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone || !/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Valid 10-digit phone number required';
    }
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.pincode || !/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Valid 6-digit pincode required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleRazorpayPayment = async () => {
    setLoading(true);
    const pricing = calculatePricing();

    try {
      const orderRes = await api.post('/payments/create-order', {
        amount: pricing.total,
        items: lineItems.map(item => ({
          product: item._id || item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size || 'L'
        }))
      });

      if (!orderRes.data.success) {
        throw new Error(orderRes.data.message);
      }

      const { orderId, keyId } = orderRes.data.data;

      const options = {
        key: keyId,
        amount: pricing.total * 100,
        currency: 'INR',
        name: 'OWLFIT',
        description: 'Streetwear Purchase',
        order_id: orderId,
        handler: async (response) => {
          try {
            const verifyRes = await api.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderData: {
                items: lineItems.map(item => ({
                  product: item._id || item.id,
                  name: item.name,
                  price: item.price,
                  quantity: item.quantity,
                  size: item.size || 'L'
                })),
                shippingAddress: formData,
                pricing
              }
            });

            if (verifyRes.data.success) {
              onOrderComplete(verifyRes.data.data.orderNumber);
            }
          } catch (error) {
            alert('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#000000'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      alert(error.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCODOrder = async () => {
    setLoading(true);
    const pricing = calculatePricing();

    try {
      const res = await api.post('/payments/cod', {
        items: lineItems.map(item => ({
          product: item._id || item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size || 'L'
        })),
        shippingAddress: formData,
        pricing
      });

      if (res.data.success) {
        onOrderComplete(res.data.data.orderNumber);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Order failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (step === 1) {
      if (validateForm()) {
        setStep(2);
      }
    } else {
      if (paymentMethod === 'razorpay') {
        handleRazorpayPayment();
      } else {
        handleCODOrder();
      }
    }
  };

  const pricing = calculatePricing();

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-3 bg-white border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-4xl font-black tracking-tighter">CHECKOUT</h1>
        </div>

        {/* Progress Steps */}
        <div className="flex gap-4 mb-8">
          <div className={`flex-1 h-2 ${step >= 1 ? 'bg-yellow-400' : 'bg-gray-300'} border-2 border-black`} />
          <div className={`flex-1 h-2 ${step >= 2 ? 'bg-yellow-400' : 'bg-gray-300'} border-2 border-black`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {step === 1 ? (
              <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
                <h2 className="text-2xl font-black mb-6 flex items-center gap-2">
                  <Truck size={28} />
                  DELIVERY ADDRESS
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block font-bold text-sm mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full p-3 border-2 ${errors.name ? 'border-red-500' : 'border-black'} font-mono`}
                      placeholder="Your full name"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block font-bold text-sm mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full p-3 border-2 ${errors.email ? 'border-red-500' : 'border-black'} font-mono`}
                      placeholder="email@example.com"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block font-bold text-sm mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full p-3 border-2 ${errors.phone ? 'border-red-500' : 'border-black'} font-mono`}
                      placeholder="10-digit mobile number"
                      maxLength={10}
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block font-bold text-sm mb-1">Address *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`w-full p-3 border-2 ${errors.address ? 'border-red-500' : 'border-black'} font-mono`}
                      placeholder="House/Flat No., Building, Street"
                      rows={2}
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>

                  <div>
                    <label className="block font-bold text-sm mb-1">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={`w-full p-3 border-2 ${errors.city ? 'border-red-500' : 'border-black'} font-mono`}
                      placeholder="City"
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>

                  <div>
                    <label className="block font-bold text-sm mb-1">State *</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={`w-full p-3 border-2 ${errors.state ? 'border-red-500' : 'border-black'} font-mono bg-white`}
                    >
                      <option value="">Select State</option>
                      {indianStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                  </div>

                  <div>
                    <label className="block font-bold text-sm mb-1">Pincode *</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      className={`w-full p-3 border-2 ${errors.pincode ? 'border-red-500' : 'border-black'} font-mono`}
                      placeholder="6-digit pincode"
                      maxLength={6}
                    />
                    {errors.pincode && <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>}
                  </div>

                  <div>
                    <label className="block font-bold text-sm mb-1">Landmark (Optional)</label>
                    <input
                      type="text"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleInputChange}
                      className="w-full p-3 border-2 border-black font-mono"
                      placeholder="Near..."
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
                <h2 className="text-2xl font-black mb-6 flex items-center gap-2">
                  <CreditCard size={28} />
                  PAYMENT METHOD
                </h2>

                <div className="space-y-4">
                  {/* Razorpay Option */}
                  {paymentConfig?.razorpayEnabled && (
                    <label
                      className={`block p-4 border-4 cursor-pointer transition-all ${
                        paymentMethod === 'razorpay'
                          ? 'border-yellow-400 bg-yellow-50 shadow-[4px_4px_0px_black]'
                          : 'border-black hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <input
                          type="radio"
                          name="payment"
                          value="razorpay"
                          checked={paymentMethod === 'razorpay'}
                          onChange={() => setPaymentMethod('razorpay')}
                          className="w-5 h-5"
                        />
                        <div className="flex-1">
                          <div className="font-black text-lg">PAY ONLINE</div>
                          <div className="text-sm text-gray-600">
                            UPI, Credit/Debit Card, Net Banking, Wallets
                          </div>
                        </div>
                        <img src="https://badges.razorpay.com/badge-light.png" alt="Razorpay" className="h-8" />
                      </div>
                    </label>
                  )}

                  {/* COD Option */}
                  <label
                    className={`block p-4 border-4 cursor-pointer transition-all ${
                      paymentMethod === 'cod'
                        ? 'border-yellow-400 bg-yellow-50 shadow-[4px_4px_0px_black]'
                        : 'border-black hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <input
                        type="radio"
                        name="payment"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={() => setPaymentMethod('cod')}
                        className="w-5 h-5"
                      />
                      <div className="flex-1">
                        <div className="font-black text-lg">CASH ON DELIVERY</div>
                        <div className="text-sm text-gray-600">
                          Pay when you receive your order (+{formatPrice(paymentConfig?.codCharge || 49)} COD charge)
                        </div>
                      </div>
                      <Truck size={32} />
                    </div>
                  </label>
                </div>

                {/* Delivery Address Summary */}
                <div className="mt-6 p-4 bg-gray-50 border-2 border-black">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-bold text-sm">DELIVERING TO:</span>
                    <button
                      onClick={() => setStep(1)}
                      className="text-sm underline font-bold"
                    >
                      Change
                    </button>
                  </div>
                  <div className="text-sm">
                    <p className="font-bold">{formData.name}</p>
                    <p>{formData.address}</p>
                    <p>{formData.city}, {formData.state} - {formData.pincode}</p>
                    <p>Phone: {formData.phone}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6 sticky top-4">
              <h2 className="text-xl font-black mb-4">ORDER SUMMARY</h2>
              
              <div className="space-y-3 mb-6 max-h-48 overflow-y-auto">
                {lineItems.map((item, idx) => (
                  <div key={`${item._id || item.id}-${item.size}-${idx}`} className="flex justify-between items-start text-sm border-b border-gray-200 pb-2">
                    <div>
                      <div className="font-bold">{item.name}</div>
                      <div className="text-gray-500 text-xs">
                        Size: {item.size || 'L'} {item.quantity > 1 && <span>· Qty {item.quantity}</span>}
                      </div>
                    </div>
                    <div className="font-bold">{formatPrice(item.price * item.quantity)}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 border-t-2 border-black pt-4">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-bold">{formatPrice(pricing.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="font-bold">
                    {pricing.shippingCharge === 0 ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      formatPrice(pricing.shippingCharge)
                    )}
                  </span>
                </div>
                {paymentMethod === 'cod' && (
                  <div className="flex justify-between text-orange-600">
                    <span>COD Charge</span>
                    <span className="font-bold">{formatPrice(pricing.codCharge)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-black pt-2 border-t border-gray-300">
                  <span>TOTAL</span>
                  <span>{formatPrice(pricing.total)}</span>
                </div>
                <div className="text-xs text-gray-500 text-right">
                  (Inclusive of all taxes)
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full mt-6 bg-yellow-400 text-black py-4 font-black text-lg border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin" size={24} />
                    PROCESSING...
                  </>
                ) : step === 1 ? (
                  'CONTINUE TO PAYMENT'
                ) : paymentMethod === 'cod' ? (
                  'PLACE ORDER (COD)'
                ) : (
                  'PAY NOW'
                )}
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                <Shield size={16} />
                <span>100% Secure Checkout</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
